import { getEnvironmentData } from '#app/shared/apis/environment'
import { getEquipmentTypes } from '#app/shared/apis/equipmentTypes'
import { type AppStateData, type Environment, setGlobalEnvironment } from '#app/shared/contexts/AppContext'
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes'
import { isCypress } from '#app/shared/utils/auth'
import { initializeDataDog } from '#app/shared/utils/datadog'
import { initializeFullStory } from '#app/shared/utils/fullstory'
import { logFatalUnexpectedError } from '#app/shared/utils/logging'
import { initializePostHog } from '#app/shared/utils/posthog'
import { assign, fromPromise, log, setup } from 'xstate'

type InputContext = {
  context: AppStateData
}

const promiseLogicGetEnvironment = fromPromise<
  { environment: Environment; equipmentTypes: EquipmentTypeResponseItem[] },
  InputContext
>(async () => {
  const [response, equipmentTypes] = await Promise.all([
    getEnvironmentData(),
    getEquipmentTypes()
  ])

  return {
    environment: response.data,
    equipmentTypes: equipmentTypes.data
  }
})

export const appMachine = setup({
  actors: {
    fetchEnvironment: promiseLogicGetEnvironment
  }
}).createMachine({
  types: {} as {
    context: AppStateData
    events: { type: 'Hydrate' }
  },
  id: 'WebApplicationState',
  initial: 'Unknown',
  context: {
    isHydrating: false,
    fetchCount: 0
  },
  states: {
    Unknown: {
      on: {
        Hydrate: {
          actions: [log('appMachine.Hydrate')],
          target: 'Boostrapping'
        }
      }
    },
    Ready: {
      entry: [
        log('appMachine.Ready'),
        ({ context }) => {
          if (isCypress()) {
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            ;(window as any).appEnvironment = context.environment
          }
          setGlobalEnvironment(context.environment)
          initializeDataDog(context.environment)
          initializeFullStory(context.environment)
          initializePostHog(context.environment)
          console.info('Environment bootstrapped', context.environment)
        }
      ]
    },
    Failed: {
      entry: [log('appMachine.Failed')]
    },
    Boostrapping: {
      entry: [
        log('appMachine.Bootstrapping'),
        assign(({ context }) => ({
          fetchCount: context.fetchCount + 1
        }))
      ],
      invoke: {
        input: ({ context }) => ({
          context: context as AppStateData
        }),
        src: 'fetchEnvironment',
        onDone: {
          target: 'Ready',
          actions: assign(({ event }) => {
            return {
              environment: event.output.environment,
              equipmentTypes: event.output.equipmentTypes
            }
          })
        },
        onError: {
          target: 'Failed',
          actions: [
            ({ event }) => {
              logFatalUnexpectedError(
                new Error('Application bootstrapping failed.'),
                { cause: event.error }
              )
            }
          ]
        }
      }
    }
  }
})
